import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import matchSorter from 'match-sorter';
import qs from 'querystring';
import * as propertyActions from '../../../actions/properties';
import * as sidebarActions from '../../../actions/sidebar';
import * as listActions from '../../../actions/lists';
import Layout from '../../../layout';
import SearchBar from '../../../components/common/SearchBar';
import Input from '../../../components/common/Input';
import {
  Tabs,
  Tab,
  TabList,
  TabPill,
  TabPanel
} from '../../../components/common/Tabs';
import { ListItem, List, CheckboxList } from '../../../components/common/List';

const propTypes = {
  hotel: PropTypes.object.isRequired,
  hotels: PropTypes.array.isRequired,
  lists: PropTypes.array.isRequired,
  fetchProperty: PropTypes.func.isRequired,
  fetchProperties: PropTypes.func.isRequired,
  fetchLists: PropTypes.func.isRequired,
  updateSidebar: PropTypes.func.isRequired,
  cloneLists: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

const Clone = ({
  hotel,
  hotels,
  lists,
  fetchProperties,
  fetchProperty,
  fetchLists,
  updateSidebar,
  cloneLists,
  match: {
    url,
    params: { hotelid }
  },
  location: { search },
  history
}) => {
  const [searchLists, setSearchLists] = useState('');
  const [searchHotels, setSearchHotels] = useState('');
  const [checked, setChecked] = useState({});
  const [listsChecked, setListsChecked] = useState({});
  useEffect(() => {
    const { selected } = qs.parse(search.slice(1));
    if (selected) {
      setListsChecked({
        ...listsChecked,
        [selected]: !listsChecked[selected]
      });
      history.replace(url);
    }
    fetchProperties({ per_page: 300 });
    fetchProperty(hotelid).then(property => {
      updateSidebar({ titles: { Hotel: property.name } });
    });
    fetchLists(hotelid, { per_page: 1000 });
    return () => {};
  }, []);

  const onSubmit = evt => {
    evt.preventDefault();

    const ids = Object.keys(listsChecked)
      .map(v => {
        return listsChecked[v] ? v : undefined;
      })
      .filter(v => v);

    const toPropertyIds = Object.keys(checked)
      .map(v => {
        return checked[v] ? v : undefined;
      })
      .filter(v => v);

    if (!ids.length || !toPropertyIds.length) {
      // TODO alert or notification that both items and properties to clone to
      // must have at least one item checkd
      return;
    }

    cloneLists(hotelid, ids, toPropertyIds).then(() => {
      history.push(`/hotels/${hotelid}/lists`);
    });
  };

  const filteredLists = matchSorter(lists, searchLists, {
    keys: ['title']
  });
  const currentLists = filteredLists.filter(list => !list.archived);
  const archivedLists = filteredLists.filter(list => list.archived);
  const filteredHotels = matchSorter(hotels, searchHotels, { keys: ['name'] });

  return (
    <Layout metaTitle="Clone a List">
      <div className="w-full max-w-3xl">
        <Link
          to={`/hotels/${hotelid}/lists`}
          className="mb-8 inline-block text-sm text-grey-dark hover:text-grey-darkest no-underline"
        >
          &lt; Back to Lists
        </Link>
        <h1 className="mb-6">Clone a List</h1>
        <div className="mb-16">
          Use this page to Clone Lists on {hotel.name}.
        </div>
        <div className="flex flex-wrap justify-between mb-16">
          <div style={{ width: 440 }}>
            <SearchBar
              id="filter-lists"
              className="mb-4"
              placeholder="Filter Lists..."
              value={searchLists}
              onChange={({ value }) => setSearchLists(value)}
            />
            <Tabs>
              <TabList>
                <Tab>
                  <span className="mr-2">Current</span>
                  <TabPill id="current-pill">{currentLists.length}</TabPill>
                </Tab>
                <Tab>
                  <span className="mr-2">Archived</span>
                  <TabPill id="archived-pill">{archivedLists.length}</TabPill>
                </Tab>
              </TabList>

              <TabPanel>
                <List id="current-list">
                  {currentLists.map(list => (
                    <ListItem
                      id={list.id}
                      key={list.id}
                      className="p-4 border-b border-grey-light flex items-center"
                    >
                      <Input
                        id={list.id}
                        type="checkbox"
                        className="mr-4"
                        input={{
                          checked: !!listsChecked[list.id],
                          onChange: () =>
                            setListsChecked({
                              ...listsChecked,
                              [list.id]: !listsChecked[list.id]
                            })
                        }}
                      />
                      <Link to={`/hotels/${hotelid}/lists/${list.id}/edit`}>
                        {list.title}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>
              <TabPanel>
                <List id="archived-list">
                  {archivedLists.map(list => (
                    <ListItem
                      id={list.id}
                      key={list.id}
                      className="p-4 border-b border-grey-light flex items-center"
                    >
                      <Input
                        id={list.id}
                        type="checkbox"
                        className="mr-4"
                        input={{
                          checked: !!listsChecked[list.id],
                          onChange: () =>
                            setListsChecked({
                              ...listsChecked,
                              [list.id]: !listsChecked[list.id]
                            })
                        }}
                      />
                      <Link to={`/hotels/${hotelid}/lists/${list.id}/edit`}>
                        {list.title}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>
            </Tabs>
          </div>
          <div style={{ width: 440 }}>
            <SearchBar
              id="filter-hotels"
              className="mb-16"
              placeholder="Filter Hotels..."
              value={searchHotels}
              onChange={({ value }) => setSearchHotels(value)}
            />
            <CheckboxList
              id="checkbox-hotels"
              title="Hotels"
              data={filteredHotels.map(hotelItem => ({
                id: hotelItem.id,
                name: hotelItem.name,
                displayName: item => (
                  <Link to={`/hotels/${item.id}`} className="mr-4">
                    {item.name}
                  </Link>
                ),
                checked: checked[hotelItem.id],
                onChange: () => {
                  setChecked({
                    ...checked,
                    [hotelItem.id]: !checked[hotelItem.id]
                  });
                }
              }))}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" className="btn" onClick={onSubmit}>
            Clone
          </button>
        </div>
      </div>
    </Layout>
  );
};

Clone.propTypes = propTypes;

export default connect(
  (state, props) => ({
    hotel: state.property.data,
    // FIXME
    hotels: state.properties.data.filter(property => property.type === 'hotel'),
    lists: state.lists.data,
    isLoading:
      state.property.isLoading ||
      state.properties.isLoading ||
      state.lists.isLoading
  }),
  {
    ...propertyActions,
    ...listActions,
    ...sidebarActions
  }
)(Clone);
